import React, { useEffect, useMemo, useState } from 'react'
import { IonGrid } from '@ionic/react'
import { Input, Label } from '@mytours/components'
import {
	Collection,
	CollectionItem,
	COLLECTION_ITEM_TYPE_COLLECTION,
	COLLECTION_LIST_LAYOUT_GRID,
	COLLECTION_SORT_BY_DISTANCE,
	sortItems,
	RootStoreState,
} from 'mytours-read-common'
import { getDirMode, getStyleProperty } from 'mobile_web/modules/platform'
import { getUserCurrentPosition } from '../../functions'
import TextPlaceholder from 'mobile_web/modules/common/components/placeholders/Text'
import classNames from 'classnames'
import { GetCollectionsShowResponse, MediaItem } from 'mytours-read-api-typescript'
import { ImageSizePreset, ImageSizePresetMap, ScreenSize } from 'common_lib/types'
import { generateImageUrl, getScreenSize } from 'common_lib/utils'
import CardImage from 'mobile_web/modules/common/components/card-image'
import { getImageHeight } from 'mobile_web/modules/screen/components/parts/functions'
import SectionMediaImage from 'mobile_web/modules/screen/components/parts/SectionMediaImage'
import { getImageAlt } from 'mobile_web/modules/common/functions'
import { fade } from 'common_lib/services/color'
import SectionMediaAudioComponent from 'mobile_web/modules/screen/components/parts/SectionMediaAudio'
import SectionMediaVideoComponent from 'mobile_web/modules/screen/components/parts/SectionMediaVideo'
import { useSelector } from 'react-redux'
import { useLiveQuery } from 'dexie-react-hooks'
import db from 'mobile_web/modules/db'
import {
	addToFavouritedKeys,
	collectionItemToFavouritedItem,
	removeFromFavouritedKeys,
} from 'mobile_web/modules/favourites/queries'
import { useDownloadedCollections } from 'mobile_web/modules/offline/useDownload'
import { useOnline } from 'mobile_web/modules/offline/useOnline'

const ListLayoutEnum = GetCollectionsShowResponse.ListLayoutEnum

interface Props {
	collection: Collection
	items: CollectionItem[]
	onItemClicked: (item: CollectionItem, openInModal?: boolean) => () => void
	loading: boolean
}
const CollectionListContent: React.FC<Props> = ({ collection = {}, items, onItemClicked, loading }) => {
	const [orderedItems, setOrderedItems] = useState<CollectionItem[]>([])
	const [screenSize, setScreenSize] = useState<ScreenSize>(getScreenSize())
	const [imageHeight, setImageHeight] = useState<number>(getImageHeight(screenSize))

	useEffect(() => {
		window.addEventListener('resize', updateScreenSize)

		return () => {
			window.removeEventListener('resize', updateScreenSize)
		}
	}, [])

	const updateScreenSize = (): void => {
		const newScreenSize = getScreenSize()

		if (newScreenSize !== screenSize) {
			setImageHeight(getImageHeight(screenSize))
			setScreenSize(newScreenSize)
		}
	}

	useEffect(() => {
		if (items.length === 0) {
			setOrderedItems([])
			return
		}
		sortItemsListener()
	}, [collection, items])

	const sortItemsListener = () => {
		// run position listener only if `sort_by` = DISTANCE
		if (!collection || collection?.sort_by !== COLLECTION_SORT_BY_DISTANCE) {
			// if `sort_by` is different then DISTANCE, just sort by provided method
			const itemsTmp = sortItems(items, collection?.sort_by)
			setOrderedItems(itemsTmp)
			return
		}

		getUserCurrentPosition(
			coordinates => {
				if (items.length === 0) {
					return
				}

				setOrderedItems(sortItems(items, collection?.sort_by, coordinates))
			},
			() => {
				setOrderedItems(sortItems(items, collection?.sort_by))
			},
		)
	}

	const renderIntroduction = useMemo(() => {
		const childrenProps = loading
			? { children: <TextPlaceholder /> }
			: { dangerouslySetInnerHTML: { __html: collection.description } }

		if (!loading && !collection.description) {
			return
		}

		return <div dir={getDirMode()} className="list-introduction list-block body-text" {...childrenProps} />
	}, [loading, collection.description])

	const renderMediaItem = useMemo(() => {
		const { preview_media_item: previewMediaItem, id, name } = collection

		if (!previewMediaItem) {
			return
		}

		let component: React.ReactElement

		switch (previewMediaItem.media_type) {
			case MediaItem.MediaTypeEnum.Audio: {
				component = (
					<SectionMediaAudioComponent
						audioPreview={previewMediaItem}
						container={{ type: 'list', id, title: name }}
						collectionId={id}
					/>
				)
				break
			}
			case MediaItem.MediaTypeEnum.Image: {
				component = (
					<div className="full-width" role="button">
						<SectionMediaImage file={previewMediaItem.file} alt={getImageAlt(previewMediaItem)} />
					</div>
				)
				break
			}
			case MediaItem.MediaTypeEnum.Video:
			case MediaItem.MediaTypeEnum.Webvideo: {
				component = (
					<SectionMediaVideoComponent
						sectionItem={previewMediaItem}
						height={imageHeight}
						isSingleItem={true}
						sectionId={id}
						sectionTitle={name}
						containerName={name}
					/>
				)
				break
			}
		}

		if (component) {
			return <div className="introduction-media-item">{component}</div>
		}

		return <div />
	}, [, collection])

	const [searchText, setSearchText] = useState<string>('')
	const filteredItems = useMemo(() => {
		if (!searchText) {
			return orderedItems
		}

		return orderedItems.filter(item => item.title.toLowerCase().includes(searchText.toLowerCase()))
	}, [orderedItems, searchText])

	const renderList = (): React.ReactElement => {
		const renderItems = loading ? Array(collection?.item_count || 4).fill({}) : filteredItems || []
		const itemTypeClass = loading ? ListLayoutEnum.Wide : collection?.list_layout || ListLayoutEnum.Grid

		const listLayout = collection?.list_layout || COLLECTION_LIST_LAYOUT_GRID
		const isListLayoutRow = listLayout === ListLayoutEnum.List

		return (
			<div className="list-block">
				<div className={classNames('list-items', itemTypeClass)}>
					{isListLayoutRow ? renderSearch(true) : null}
					{renderItems.map((item: CollectionItem, index: number) => renderItem(item, index))}
				</div>
			</div>
		)
	}

	const renderSearch = (isListRow: boolean): React.ReactElement => {
		if (loading) {
			return null
		}
		if (!collection.search_enabled) {
			return null
		}
		const borderColor = fade(getStyleProperty('--body-color'), 0.25)

		return (
			<div className={classNames('list-search', !isListRow ? 'list-search-wide-grid' : null)}>
				<Input
					variant="ios"
					value={searchText}
					onChange={e => setSearchText(e.target.value)}
					placeholder="Search..."
					className="list-search-ion-input"
					style={{ borderColor }}
				/>
				<div className="list-search-icons-container">
					<i className="fal fa-magnifying-glass list-search-loupe" aria-label="Search" aria-hidden="true"></i>
					<div className="list-search-close-container" onClick={() => setSearchText('')}>
						<i className="fal fa-times list-search-close" aria-label="Close" aria-hidden="true"></i>
					</div>
				</div>
			</div>
		)
	}

	const getImageSize = () => {
		const imageSizePreset = ImageSizePreset.Row

		return {
			imageSizePreset,
			...ImageSizePresetMap[imageSizePreset],
		}
	}

	const appMetaData = useSelector((state: RootStoreState) => state.settings.appMetaData)
	const language = useSelector((state: RootStoreState) => state.settings.selectedLanguage?.code) || 'en'
	const online = useOnline()
	const downloaded = useDownloadedCollections(language)
	const dexieFavouritesKeys = useLiveQuery(() => db.favouritedKeys.toArray())
	const favouritesKeys = dexieFavouritesKeys ? dexieFavouritesKeys.map(key => key.key) : []

	const renderItem = (item: CollectionItem, index: number): React.ReactNode => {
		const key = `${item.item_id}-${item.item_type}`
		// Only a tour can be downloaded, so only a tour can be missing one; everything else needs the
		// network either way and saying so on each card would be noise.
		const unavailableOffline =
			!online && item.item_type === COLLECTION_ITEM_TYPE_COLLECTION && !downloaded.has(Number(item.item_id))
		const isFavourited = favouritesKeys.includes(key)
		const toggleFavorite: React.MouseEventHandler<HTMLButtonElement> = e => {
			e.stopPropagation()
			const favouritedItem = collectionItemToFavouritedItem(item)
			if (!isFavourited) {
				addToFavouritedKeys(key, favouritedItem)
			} else {
				removeFromFavouritedKeys(key)
			}
		}

		const size = getImageSize()

		const listLayout = collection?.list_layout || COLLECTION_LIST_LAYOUT_GRID

		const coverImageGridFocalPoint = item?.cover_image_grid?.file?.focal_point
		const coverImageFocalPoint = item?.cover_image?.file?.focal_point
		const coverImageGridCrop =
			coverImageGridFocalPoint && (coverImageGridFocalPoint[0] !== 0.5 || coverImageGridFocalPoint[1] !== 0.5)
		const coverImageCrop = coverImageFocalPoint && (coverImageFocalPoint[0] !== 0.5 || coverImageFocalPoint[1] !== 0.5)

		const coverImageGridOptions = {
			crop: coverImageGridCrop,
			focalPoint: coverImageGridFocalPoint,
		}

		const coverImageOptions = {
			crop: coverImageCrop,
			focalPoint: coverImageFocalPoint,
		}

		const coverImageGrid =
			item?.cover_image_grid_url_display ||
			(item?.cover_image_grid?.file &&
				generateImageUrl(item?.cover_image_grid.file, {
					...size,
					...coverImageGridOptions,
				}))

		const coverImageGrid2x =
			item?.cover_image_grid?.file &&
			generateImageUrl(item?.cover_image_grid.file, {
				width: size.width * 2,
				height: size.height * 2,
				...coverImageGridOptions,
			})

		const coverImage =
			item?.cover_image_url_display ||
			(item?.cover_image?.file &&
				generateImageUrl(item?.cover_image.file, {
					...size,
					...coverImageOptions,
				}))

		const coverImage2x =
			item?.cover_image?.file &&
			generateImageUrl(item?.cover_image.file, {
				width: size.width * 2,
				height: size.height * 2,
				...coverImageOptions,
			})

		switch (listLayout) {
			case ListLayoutEnum.Grid: {
				return (
					<CardImage
						key={index}
						variant="grid"
						loading={loading}
						className="collection-list-item list-card-image"
						image={coverImageGrid ? coverImageGrid : coverImage}
						image2x={coverImageGrid2x ? coverImageGrid2x : coverImage2x}
						label={item.title}
						role="button"
						onClick={onItemClicked(item)}
						itemId={item.item_id}
						itemType={item.item_type}
						item={item}
						appMetaData={appMetaData}
						toggleFavorite={toggleFavorite}
						isFavourited={isFavourited}
						unavailableOffline={unavailableOffline}
					/>
				)
			}

			case ListLayoutEnum.Wide: {
				return (
					<CardImage
						key={index}
						variant="wide"
						loading={loading}
						className="collection-list-item list-card-image"
						image={coverImage ? coverImage : coverImageGrid}
						image2x={coverImage2x ? coverImage2x : coverImageGrid2x}
						label={item.title}
						role="button"
						onClick={onItemClicked(item)}
						itemId={item.item_id}
						itemType={item.item_type}
						item={item}
						appMetaData={appMetaData}
						toggleFavorite={toggleFavorite}
						isFavourited={isFavourited}
						unavailableOffline={unavailableOffline}
					/>
				)
			}
			case ListLayoutEnum.List: {
				return (
					// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- as the row was
					<Label
						variant="ios"
						as="span"
						key={index}
						className="collection-list-item list-row-item"
						onClick={onItemClicked(item)}
					>
						{item?.title || ''}
					</Label>
				)
			}
			default: {
				return null
			}
		}
	}

	const listLayout = collection?.list_layout || COLLECTION_LIST_LAYOUT_GRID
	const isListLayoutRow = listLayout === ListLayoutEnum.List

	return (
		<IonGrid className="list-container">
			{renderIntroduction}
			{renderMediaItem}
			{!isListLayoutRow ? renderSearch(false) : null}
			{renderList()}
		</IonGrid>
	)
}

export default CollectionListContent
